/**
 * Pure view model for /account. Turns the three reads (profile, awards,
 * pools) into everything the screen renders, so every state (loading,
 * failed, empty, populated) is testable without a router or a network.
 */
import type { typeAwardChip } from "../awards/awardChips";
import { type typeAwardCounts, ZERO_AWARD_COUNTS } from "../awards/awardCounts";
import {
  toPoolCardModel,
  type typePoolCardModel,
  type typePoolListRow,
} from "../pools/poolsListState";

export type typeAccountHomeView = {
  isProfileLoading: boolean;
  hasProfileFailed: boolean;
  displayName: string;
  username: string;
  photoUrl: string | null;
  favoriteTeamIds: readonly string[];
  areAwardsLoading: boolean;
  haveAwardsFailed: boolean;
  thisSeason: typeAwardCounts;
  allTime: typeAwardCounts;
  chips: readonly typeAwardChip[];
  arePoolsLoading: boolean;
  havePoolsFailed: boolean;
  poolCards: readonly typePoolCardModel[];
  poolNameById: Readonly<Record<string, string>>;
};

export function buildAccountHomeView(input: {
  isProfileLoading: boolean;
  hasProfileFailed: boolean;
  profile:
    | {
        accountId: string;
        username: string;
        displayName: string;
        photoUrl?: string | null;
        favoriteTeamIds: string[];
      }
    | null
    | undefined;
  areAwardsLoading: boolean;
  haveAwardsFailed: boolean;
  awards:
    | {
        thisSeason: typeAwardCounts;
        allTime: typeAwardCounts;
        awards: readonly typeAwardChip[];
      }
    | null
    | undefined;
  arePoolsLoading: boolean;
  havePoolsFailed: boolean;
  poolItems: readonly typePoolListRow[] | null | undefined;
}): typeAccountHomeView {
  const poolItems = input.poolItems ?? [];

  const poolNameById: Record<string, string> = {};
  for (const item of poolItems) {
    poolNameById[item.poolId] = item.name;
  }

  return {
    isProfileLoading: input.isProfileLoading,
    hasProfileFailed: input.hasProfileFailed,
    displayName: input.profile?.displayName ?? "",
    username: input.profile?.username ?? "",
    photoUrl: input.profile?.photoUrl ?? null,
    favoriteTeamIds: input.profile?.favoriteTeamIds ?? [],
    areAwardsLoading: input.areAwardsLoading,
    haveAwardsFailed: input.haveAwardsFailed,
    thisSeason: input.awards?.thisSeason ?? ZERO_AWARD_COUNTS,
    allTime: input.awards?.allTime ?? ZERO_AWARD_COUNTS,
    chips: input.awards?.awards ?? [],
    arePoolsLoading: input.arePoolsLoading,
    havePoolsFailed: input.havePoolsFailed,
    // ISSUE-770 gave `toPoolCardModel` an optional second (catalog) argument.
    // `.map(toPoolCardModel)` would pass Array#map's index as that argument,
    // so this wraps the call. `AccountPoolsList` never renders
    // `leagueLabel`, so the omitted catalog data is never visibly wrong.
    poolCards: poolItems.map((item) => toPoolCardModel(item)),
    poolNameById,
  };
}
